import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { CuentaTrabajoVes } from '../../interfaces/cuenta-trabajo-ves.interface';
import { CuentaTrabajo } from '../../interfaces/cuenta-trabajo.interface';
import { CuentaTrabajoVesService } from '../../services/cuenta-trabajo-ves.service';
import { WorkingAccountService } from '../../services/working-account.service';
import { ParameterService } from '../../services/parameter.service';
import { AddWorkingAccountComponent } from '../working-account/add-working-account/add-working-account.component';

// Interfaz extendida que combina CuentaTrabajo con el estado de CuentaTrabajoVes
interface CuentaTrabajoExtended extends CuentaTrabajo {
  cuentaVesId?: number; // ID de la cuenta en cuenta_trabajo_ves
  activaEnVes?: boolean; // Estado activa en cuenta_trabajo_ves
}

@Component({
  selector: 'app-cuentas-activas-ves',
  templateUrl: './cuentas-activas-ves.component.html',
  styleUrls: ['./cuentas-activas-ves.component.sass'],
})
export class CuentasActivasVesComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription();
  listaCuentas: CuentaTrabajoExtended[] = [];
  expandedCardId: number | null = null;
  asignacionMaestraId: number | null = null;
  displayedColumns: string[] = [
    'nombre',
    'apellidos',
    'banco',
    'pais',
    'tipoCuenta',
    'numeroCuenta',
    'activa',
    'asignacionMaestra',
    'acciones',
  ];

  constructor(
    private workingAccountService: WorkingAccountService,
    private cuentaTrabajoVesService: CuentaTrabajoVesService,
    private dialog: MatDialog,
    private spinner: SpinnerService,
    private snackBar: MatSnackBar,
    private parameterService: ParameterService
  ) {}

  ngOnInit(): void {
    this.loadCuentas();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async loadCuentas() {
    const spinnerRef = this.spinner.start();
    try {
      const cuentasTrabajo =
        await this.workingAccountService.getWorkingAccounts(['VE']);

      const cuentasVes =
        await this.cuentaTrabajoVesService.getAllCuentaTrabajoVes();
      const cuentasVesMap = new Map<number, CuentaTrabajoVes>();
      cuentasVes.forEach((cv) => {
        cuentasVesMap.set(cv.cuenta_trabajo_id, cv);
      });

      this.listaCuentas = cuentasTrabajo.map((ct) => {
        const cuentaVes = cuentasVesMap.get(ct.cuenta_trabajo_id);
        return {
          ...ct,
          cuentaVesId: cuentaVes?.cuenta_trabajo_ves_id,
          activaEnVes: cuentaVes?.activa ?? false,
        };
      });

      this.listaCuentas.sort((a, b) => {
        return (a.banco?.bancoID ?? 0) - (b.banco?.bancoID ?? 0);
      });

      try {
        const param = await this.parameterService.getParameter('ASIGNACION_MAESTRA_VES');
        const valor = String(param?.valor ?? param?.value ?? 'NA').trim();
        const parsedId = parseInt(valor, 10);
        this.asignacionMaestraId = (valor !== 'NA' && !isNaN(parsedId) && parsedId > 0) ? parsedId : null;
      } catch {
        this.asignacionMaestraId = null;
      }
    } catch (error) {
      this.snackBar.open('Error al cargar las cuentas', 'Ok', {
        duration: 3000,
      });
    } finally {
      this.spinner.stop(spinnerRef);
    }
  }

  openCreateDialog(cuenta?: CuentaTrabajo): void {
    const dialogRef = this.dialog.open(AddWorkingAccountComponent, {
      data: {
        type: cuenta ? 'UPDATE' : 'CREATE',
        cuenta,
      },
      height: '70%',
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        await this.loadCuentas();
      }
    });
  }

  async toggleActiva(cuenta: CuentaTrabajoExtended) {
    if (!cuenta.cuenta_trabajo_id) {
      this.snackBar.open('ID de cuenta inválido', 'Ok', { duration: 3000 });
      return;
    }
    const spinnerRef = this.spinner.start();
    try {
      const nuevoEstado = !cuenta.activaEnVes;
      if (cuenta.cuentaVesId) {
        let responseCuentaVesUpdate =
          await this.cuentaTrabajoVesService.updateCuentaTrabajoVes(
            cuenta.cuentaVesId,
            { activa: nuevoEstado }
          );
      } else {
        const result =
          await this.cuentaTrabajoVesService.createCuentaTrabajoVes({
            cuenta_trabajo_id: cuenta.cuenta_trabajo_id,
          });
        cuenta.cuentaVesId = result.cuenta_trabajo_ves_id;
      }

      cuenta.activaEnVes = nuevoEstado;

      this.snackBar.open(
        `Cuenta ${nuevoEstado ? 'activada' : 'desactivada'} correctamente`,
        'Ok',
        { duration: 3000 }
      );

      await this.loadCuentas();
    } catch (error) {
      this.snackBar.open('Error al cambiar el estado de la cuenta', 'Ok', {
        duration: 3000,
      });
      await this.loadCuentas();
    } finally {
      this.spinner.stop(spinnerRef);
    }
  }

  esMaestra(cuenta: CuentaTrabajoExtended): boolean {
    return this.asignacionMaestraId !== null && +this.asignacionMaestraId === +cuenta.cuenta_trabajo_id;
  }

  esMaestraDisabled(cuenta: CuentaTrabajoExtended): boolean {
    return this.asignacionMaestraId !== null && +this.asignacionMaestraId !== +cuenta.cuenta_trabajo_id;
  }

  async toggleAsignacionMaestra(cuenta: CuentaTrabajoExtended) {
    if (!cuenta.cuenta_trabajo_id) {
      this.snackBar.open('ID de cuenta inválido', 'Ok', { duration: 3000 });
      return;
    }
    const spinnerRef = this.spinner.start();
    try {
      const esMaestraActual = this.esMaestra(cuenta);
      const nuevoValor = esMaestraActual ? 'NA' : String(cuenta.cuenta_trabajo_id);

      await this.parameterService.saveParameter('ASIGNACION_MAESTRA_VES', nuevoValor);
      this.asignacionMaestraId = esMaestraActual ? null : cuenta.cuenta_trabajo_id;

      this.snackBar.open(
        esMaestraActual
          ? 'Asignación maestra desactivada'
          : `Asignación maestra asignada a ${cuenta.nombre} ${cuenta.apellidos}`,
        'Ok',
        { duration: 3000 }
      );

      await this.loadCuentas();
    } catch (error) {
      this.snackBar.open('Error al actualizar la asignación maestra', 'Ok', {
        duration: 3000,
      });
    } finally {
      this.spinner.stop(spinnerRef);
    }
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  async eliminarCuenta(cuenta: CuentaTrabajo) {
    if (!cuenta.cuenta_trabajo_id) return;

    const spinnerRef = this.spinner.start();
    try {
      await this.workingAccountService.inactivateAccount(
        cuenta.cuenta_trabajo_id
      );
      this.snackBar.open('Cuenta eliminada correctamente', 'Ok', {
        duration: 3000,
      });
      await this.loadCuentas();
    } catch (error) {
      this.snackBar.open('Error al eliminar la cuenta', 'Ok', {
        duration: 3000,
      });
    } finally {
      this.spinner.stop(spinnerRef);
    }
  }
}
